import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { notify } from "../../utils/notify"

// reactstrap components
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Label,
  Row,
  Modal,
  ModalBody,
  ModalFooter,
} from 'reactstrap'
// core components
import SimpleHeader from 'components/Headers/SimpleHeader.js'
import { useDashboardContext } from '../../contexts/DashboardContext'
import { useAccessControlContext } from '../../contexts/AccessControlContext'

function SimDetails({ location }) {
  const { getDealerSimDetails, getProductList, updateDealerSimDetails, cancelOrder } =
    useDashboardContext()
  const { getSimDetailsFeatures } = useAccessControlContext()
  const [simDetailsFeatures] = useState(
    getSimDetailsFeatures()
  )
  const history = useHistory()
  const [iccid, setIccid] = useState(null)
  const [msisdn, setMsisdn] = useState(null)
  const [costCtrName, setCostCtrName] = useState(null)
  const [costCtrType, setCostCtrType] = useState(null)

  const [curStatusId, setCurStatusId] = useState(null)
  const [curStatus, setCurStatus] = useState(null)
  const [targetStatus, setTargetStatus] = useState(null)
  const [targetStatusId, setTargetStatusId] = useState(null)
  const [showStatusList, setShowStatusList] = useState(false)

  const [curPlanCosId, setCurPlanCosId] = useState(null)
  const [curPlanName, setCurPlanName] = useState(null)
  const [targetPlanCosId, setTargetPlanCosId] = useState(null)
  const [targetPlanName, setTargetPlanName] = useState(null)
  const [planList, setPlanList] = useState([])
  const [showPlanList, setShowPlanList] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editable, setEditable] = useState(true)

  const [canReset, setCanReset] = useState(false)
  const [resetting, setResetting] = useState(false)

  const [orderId, setOrderId] = useState(null)
  const [orderStatus, setOrderStatus] = useState(null)

  // This could be buggy if there are changes statusId-to-statusDescr mapping.
  // The current design is based on the following mapping in status table:
  // {1: 'Loaded', 2: 'Linked', 3: 'Exported', 4: 'Activated', 5: 'Assigned', 6: 'Disabled', 7: 'Deactivated', 8: 'Expired'}
  // in the web view, we show 2 as Loaded, 4 as Available, 5 as Used, 6 as Hold, 7 as Terminated, others won't show
  const [allowedTargetStatusList, setAllowedTargetStatusList] = useState([])
  const [statusList] = useState([
    '',
    'Loaded',
    '',
    'Available',
    'Used',
    'Hold',
    'Terminated',
    ''
  ])

  // Only execute once when clicking on one record on the inventory page
  useEffect(() => {
    setIccid(location.state.sim)
    setMsisdn(location.state.msisdn)
    setCostCtrName(location.state.costCtrName)
  }, [])

  // Only execute once when entering the sim details page
  useEffect(() => {
    if (iccid !== null && iccid !== undefined) {
      getDealerSimStatus()
    }
  }, [iccid])

  const captalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  }

  const getDealerSimStatus = async () => {
    try {
      const res = await getDealerSimDetails({
        // iccidFilter is the last 10 digits of the iccid
        iccidFilter: iccid.slice(-10),
      })

      setCurStatus(res.statusDescr)
      setCurStatusId(res.statusId.toString())
      setTargetStatusId(res.statusId.toString())
      setTargetStatus(res.statusDescr)
      setCurPlanCosId(res.cosId)
      setCurPlanName(res.planName)
      setTargetPlanCosId(res.cosId)
      setTargetPlanName(res.planName)
      setCostCtrType(captalizeFirstLetter(res.costCtrType))
      setCanReset(res.orderStatus?.toLowerCase() === 'pending')
      setOrderId(res.orderId || null)
      setOrderStatus(res.orderStatus || null)
      if (
        res.orderId !== null ||
        res.dnaStatus === 'Reserved'
      ) {
        setCurStatus('Reserved')
        setTargetStatus('Reserved')
        setEditable(false)
      }
    } catch (err) {
      notify('Failed to get sim details, err: ' + err, 'error')
    }
  }


  // when the current status is in [1,2,3,4,6], we allow them to change the status to [2,4,6,7]
  // when the status is in [5,7], we don't allow them to change the status
  // status 8 will not happen
  useEffect(() => {
    if (['1', '2', '3', '4', '6'].includes(curStatusId)) {
      setAllowedTargetStatusList(['Loaded', 'Available', 'Hold', 'Terminated'])
    } else if (['5', '7'].includes(curStatusId)) {
      setAllowedTargetStatusList([])
    }
  }, [curStatus, curStatusId])

  const getTargetStatusId = (status) => {
    return (statusList.indexOf(status) + 1).toString()
  }

  const getProducts = async () => {
    var productType = 'Plan'
    var webAvailable = 'No'
    if (
      curPlanName !== null &&
      curPlanName !== undefined &&
      curPlanName === 'PAYG Plan'
    ) {
      webAvailable = 'Yes'
    }

    try {
      const res = await getProductList({
        productType: productType,
        webAvailable: webAvailable,
      })
      setPlanList(res)
    } catch (err) {
      notify('Failed to get product list, err: ' + err, 'error')
    }
  }

  const updateSimDetails = async () => {
    try {
      await updateDealerSimDetails({
        serialNumber: iccid,
        cosId: targetPlanCosId,
        statusId: targetStatusId,
      })

      setCurStatusId(targetStatusId)
      setCurStatus(targetStatus)
      setCurPlanCosId(targetPlanCosId)
      setCurPlanName(targetPlanName)
      setEditing((editing) => !editing)
      notify('Sim details updated successfully')
    } catch (err) {
      notify('Failed to update sim details, err:' + err, 'error')
      handleCancel()
    }
  }

  // The trigger function behind the edit button
  const handleEdit = () => {
    setEditing((editing) => !editing)
  }

  // The trigger function behind the activate button
  const handleActivate = () => {
    // jump to the activation page, auto-fill the msisdn
    history.push({
      pathname: '/admin/search/activate',
      state: {
        iccid: iccid.slice(-10)
      }
    })
  }

  // trigger function behind the cancel button
  const handleCancel = () => {
    setEditing((editing) => !editing)
    setTargetStatusId(curStatusId)
    setTargetStatus(curStatus)
    setTargetPlanCosId(curPlanCosId)
    setTargetPlanName(curPlanName)
  }

  // trigger function behind the save button
  const handleSave = () => {
    updateSimDetails()
  }

  const resetToggle = () => {
    setResetting((prevState) => !prevState)
  }

  const handleResetOrder = async () => {
    try {
      await cancelOrder({
        serialNumber: iccid
      })

      notify('Order reset successfully')
      setCanReset(false)
      resetToggle()
      setOrderId(null)
      setEditable(true)
      setCurStatus('Available')
      setCurStatusId('4')
    } catch (err) {
      notify('Failed to reset order, err: ' + err, 'error')
      resetToggle()
    }
  }

  return (
    <>
      <SimpleHeader name="Sim Details" />
      <Container fluid>
        <Card>
          <CardHeader>
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0 text-primary neue-bold letter-2">
                  Edit or activate this SIM
                </h3>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="text-primary ml-3">
            <Form>
              <FormGroup row>
                <Label className="letter-4" htmlFor="input-nickname" md={2}>
                  ICCID
                </Label>
                <Col className="d-flex align-items-center" md={10}>
                  <h3>{iccid}</h3>
                </Col>
              </FormGroup>

              <FormGroup row>
                <Label className="letter-4" htmlFor="input-nickname" md={2}>
                  MSISDN
                </Label>
                <Col className="d-flex align-items-center" md={10}>
                  <h3>{msisdn}</h3>
                </Col>
              </FormGroup>

              <FormGroup row>
                <Label className="letter-4" htmlFor="input-nickname" md={2}>
                  Status
                </Label>

                {!editing ? (
                  <Col className="d-flex align-items-center" md={10}>
                    <h3>{curStatus ? targetStatus : 'Select a status'}</h3>
                  </Col>
                ) : (
                  <Dropdown
                    isOpen={showStatusList}
                    disabled={!editing}
                    toggle={() => setShowStatusList(!showStatusList)}
                  >
                    <DropdownToggle color="light" caret>
                      {curStatus ? targetStatus : 'Select a status'}
                    </DropdownToggle>
                    <DropdownMenu>
                      {allowedTargetStatusList.map((status) => {
                        return (
                          <DropdownItem
                            key={status}
                            onClick={() => {
                              setTargetStatus(status)
                              setTargetStatusId(getTargetStatusId(status))
                            }}
                          >
                            {status}
                          </DropdownItem>
                        )
                      })}
                    </DropdownMenu>
                  </Dropdown>
                )}
              </FormGroup>

              {/* {editable || !editable ? ( */}
                <FormGroup row>
                  <Label className="letter-4" htmlFor="input-nickname" md={2}>
                    Plan Name
                  </Label>

                  {!editing ? (
                    <Col className="d-flex align-items-center" md={10}>
                      <h3>{curPlanName ? targetPlanName : 'Select a plan'}</h3>
                    </Col>
                  ) : (
                    <Dropdown
                      isOpen={showPlanList}
                      disabled={!editing}
                      toggle={() => {
                        setShowPlanList(!showPlanList)
                        getProducts()
                      }}
                    >
                      <DropdownToggle color="light" caret>
                        {curPlanName ? targetPlanName : 'Select a plan'}
                      </DropdownToggle>
                      <DropdownMenu>
                        {planList.map((plan) => {
                          return (
                            <DropdownItem
                              key={plan.cosId}
                              onClick={() => {
                                setTargetPlanCosId(plan.cosId)
                                setTargetPlanName(plan.planName)
                              }}
                            >
                              {plan.planName}
                            </DropdownItem>
                          )
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  )}
                </FormGroup>
              {/* ) : null} */}

              {orderId ? (
                <FormGroup row>
                  <Label className="letter-4" htmlFor="input-nickname" md={2}>
                    {'Order ID'}
                  </Label>
                  <Col className="d-flex align-items-center" md={10}>
                    <h3>{orderId}</h3>
                  </Col>
                </FormGroup>
              ) : null}

              {orderId ? (
                <FormGroup row>
                  <Label className="letter-4" htmlFor="input-nickname" md={2}>
                    {'Order Status'}
                  </Label>
                  <Col className="d-flex align-items-center" md={10}>
                    <h3>{orderStatus}</h3>
                  </Col>
                </FormGroup>
              ) : null}

              {editable ? (
                <FormGroup row>
                  <Label className="letter-4" htmlFor="input-nickname" md={2}>
                    {costCtrType}
                  </Label>
                  <Col className="d-flex align-items-center" md={10}>
                    <h3>{costCtrName}</h3>
                  </Col>
                </FormGroup>
              ) : (
                <FormGroup row>
                  <Label className="letter-4" htmlFor="input-nickname" md={2}>
                    {'Reason'}
                  </Label>
                  <Col className="d-flex align-items-center" md={10}>
                    <h3>
                      {orderId
                        ? 'On hold by an active order'
                        : 'On hold by a dealer activation'}
                    </h3>
                  </Col>
                </FormGroup>
              )}

              <Row className="mt-3 ml-1">
                {editing ? (
                  <>
                    <Button
                      onClick={(e) => {
                        e.preventDefault()
                        handleCancel()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-red"
                      onClick={(e) => {
                        e.preventDefault()
                        handleSave()
                      }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="edit"
                      disabled={!editable}
                      style={editable ? {} : { backgroundColor: 'lightgray' }}
                      onClick={(e) => {
                        e.preventDefault()
                        handleEdit()
                      }}
                    >
                      Edit
                    </Button>
                    {(JSON.parse(simDetailsFeatures?.showActivateSvcButton) && curStatus === 'Available') ? (
                      <Button
                        type="activate"
                        style={
                          curStatusId === '4'
                            ? {}
                            : { backgroundColor: 'lightgray' }
                        }
                        disabled={!(curStatusId === '4')}
                        onClick={(e) => {
                          e.preventDefault()
                          handleActivate()
                        }}
                      >
                        Activate Service
                      </Button>
                    ) : null}
                    {canReset && (
                      <Button
                        onClick={(e) => {
                          e.preventDefault()
                          resetToggle()
                        }}
                      >
                        Reset
                      </Button>
                    )}
                    {resetting ? (
                      <Modal
                        isOpen={resetting}
                        toggle={resetToggle}
                        className="modal-dialog-centered"
                      >
                        <ModalBody>
                          <p>
                            Are you sure you want to reset the order?
                          </p>
                        </ModalBody>
                        <ModalFooter>
                          <Button
                            color="secondary"
                            onClick={handleResetOrder}
                          >
                            Confirm
                          </Button>
                          <Button
                            color="primary"
                            onClick={resetToggle}
                          >
                            Cancel
                          </Button>
                        </ModalFooter>
                      </Modal>
                    ) : null}
                  </>
                )}
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </>
  )
}

export default SimDetails
